import { parseErrorResponse } from '../utils/validation';

const BASE = '';

/**
 * Custom error class for auth errors with field information
 * MUST be defined before login() and register() that use it
 */
export class AuthError extends Error {
    constructor(message, fieldErrors = {}, statusCode = 400) {
        super(message);
        this.name = 'AuthError';
        this.fieldErrors = fieldErrors;
        this.statusCode = statusCode;
    }
}

/**
 * Authentication service with improved error handling
 * - Handles field-specific errors from backend
 * - Properly checks HTTP status codes
 * - Securely stores tokens
 */

/**
 * Login with email or username
 * @param {string} emailOrUsername - Email or username
 * @param {string} password - Password (case-sensitive)
 * @throws {AuthError} With field-specific error information
 */
export async function login(emailOrUsername, password) {
    const res = await fetch(`${BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrUsername, password })
    });

    // Parse response body
    let responseData = {};
    try {
        responseData = await res.json();
    } catch (e) {
        // Response is not JSON
    }

    // Handle different status codes
    if (res.status === 200) {
        // Success - store token securely
        if (responseData.token) {
            localStorage.setItem('token', responseData.token);
        }
        if (responseData.role) localStorage.setItem('role', responseData.role);
        if (responseData.userId) localStorage.setItem('userId', responseData.userId);
        if (responseData.publicName) localStorage.setItem('publicName', responseData.publicName);
        if (responseData.username) localStorage.setItem('username', responseData.username);
        return responseData;
    }

    if (res.status === 400) {
        // Validation error - show field-specific error
        const errors = parseErrorResponse(responseData);
        const error = new AuthError(
            errors.general || 'Validationsfejl',
            errors,
            res.status
        );
        throw error;
    }

    if (res.status === 401) {
        // Authentication failed - invalid credentials
        const error = new AuthError(
            responseData.error || 'Forkert brugernavn/email eller kodeord',
            { general: responseData.error || 'Forkert brugernavn/email eller kodeord' },
            res.status
        );
        throw error;
    }

    if (res.status === 500) {
        // Server error
        const error = new AuthError(
            'Serverfejl - prøv igen senere',
            { general: 'Serverfejl' },
            res.status
        );
        throw error;
    }

    // Other status codes
    const error = new AuthError(
        responseData.error || 'Logind mislykkedes',
        parseErrorResponse(responseData),
        res.status
    );
    throw error;
}

/**
 * Register (signup) new user
 * @param {object} data - { email, username, password }
 * @throws {AuthError} With field-specific error information
 */
export async function register(data) {
    const res = await fetch(`${BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
    });

    // Parse response body
    let responseData = {};
    try {
        responseData = await res.json();
    } catch (e) {
        // Response is not JSON
    }

    // Handle different status codes
    if (res.status === 201) {
        // Success - store token securely
        if (responseData.token) {
            localStorage.setItem('token', responseData.token);
        }
        if (responseData.role) localStorage.setItem('role', responseData.role);
        if (responseData.userId) localStorage.setItem('userId', responseData.userId);
        if (responseData.publicName) localStorage.setItem('publicName', responseData.publicName);
        if (responseData.username) localStorage.setItem('username', responseData.username);
        return responseData;
    }

    if (res.status === 400) {
        // Validation error - show field-specific error
        const errors = parseErrorResponse(responseData);
        const error = new AuthError(
            errors.general || 'Validationsfejl',
            errors,
            res.status
        );
        throw error;
    }

    if (res.status === 500) {
        // Server error
        const error = new AuthError(
            'Serverfejl - prøv igen senere',
            { general: 'Serverfejl' },
            res.status
        );
        throw error;
    }

    // Other status codes
    const error = new AuthError(
        responseData.error || 'Registrering mislykkedes',
        parseErrorResponse(responseData),
        res.status
    );
    throw error;
}

/**
 * Logout user
 */
export function logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userId');
    localStorage.removeItem('publicName');
    localStorage.removeItem('username');
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
    return !!localStorage.getItem('token');
}

/**
 * Get current user info from localStorage
 */
export function getCurrentUser() {
    return {
        token: localStorage.getItem('token'),
        role: localStorage.getItem('role'),
        userId: localStorage.getItem('userId'),
        publicName: localStorage.getItem('publicName'),
        username: localStorage.getItem('username')
    };
}
